import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

/**
 * Server-side cart pricing (Phase 3).
 *
 * Calls the `compute-cart-total` edge function which validates ownership,
 * fetches authoritative course prices, applies bundle discounts, then
 * optional discount code, promotions, and Silva Coins. The frontend MUST
 * NOT compute the final number — it only displays what the server returns.
 */

export interface CartLineItem {
  course_id: string;
  title: string;
  slug: string;
  thumbnail_url: string | null;
  unit_price: number;
  bundle_id: string | null;
  bundle_title: string | null;
  bundle_discount_pct: number;
  line_total: number;
}

export interface CartTotals {
  subtotal: number;
  bundle_discount: number;
  promo_discount: number;
  code_discount: number;
  silva_coin_discount: number;
  total: number;
  currency: "BRL";
}

interface ComputeResult {
  items: CartLineItem[];
  totals: CartTotals;
}

interface Args {
  cartId: string | null;
  discountCode?: string;
  silvaCoinCode?: string;
  enabled?: boolean;
}

export function useCartTotals({ cartId, discountCode, silvaCoinCode, enabled = true }: Args) {
  const { user } = useAuth();

  return useQuery<ComputeResult>({
    queryKey: ["cart-totals", cartId, discountCode ?? "", silvaCoinCode ?? ""],
    queryFn: async () => {
      if (!cartId) {
        return {
          items: [],
          totals: {
            subtotal: 0,
            bundle_discount: 0,
            promo_discount: 0,
            code_discount: 0,
            silva_coin_discount: 0,
            total: 0,
            currency: "BRL" as const,
          },
        };
      }
      const { data, error } = await supabase.functions.invoke("compute-cart-total", {
        body: { cartId, discountCode, silvaCoinCode },
      });
      if (error) throw error;
      return data as ComputeResult;
    },
    enabled: Boolean(user) && enabled,
    staleTime: 15_000,
  });
}
